import { PartFinderBatch } from '@/api/generatedApi';
import { getBatchLabel } from '@/components/PartFinder/batchLabel';
import { useClearPartFinderBatchName } from '@/hooks/api/part-finder-batches/useClearPartFinderBatchName';
import { useUpdatePartFinderBatchName } from '@/hooks/api/part-finder-batches/useUpdatePartFinderBatchName';
import { useGetCurrentUser } from '@/hooks/api/users/useGetCurrentUser';
import { getApiError } from '@/types/apiError';
import { CheckOutlined, CloseOutlined, EditOutlined } from '@mui/icons-material';
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useEffect, useState } from 'react';

const MAX_NAME_LENGTH = 512;
const ROW_HEIGHT = '40px';

const TitleRow = styled(Box)({ display: 'flex', alignItems: 'center', gap: '4px', minHeight: ROW_HEIGHT });

const HoverRow = styled(TitleRow)(({ theme }) => ({
  position: 'relative',
  '& .edit-affordance': {
    opacity: 0,
    transition: theme.transitions.create('opacity', { duration: theme.transitions.duration.shortest }),
  },
  '&:hover .edit-affordance, &:focus-within .edit-affordance': { opacity: 1 },
}));

const FieldRow = styled(Box)({ display: 'flex', alignItems: 'center', minHeight: ROW_HEIGHT });

const InlineActions = styled(InputAdornment)({ gap: '2px' });

export const EditableBatchName = ({ batch }: { batch: PartFinderBatch }) => {
  const savedName = batch.name ?? '';
  const defaultTitle = `Batch · ${batch.breakdown.total} parts`;

  const { permissions } = useGetCurrentUser();
  const canEdit = permissions?.canEditPartFinderReport ?? false;

  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(savedName);
  const updateMutation = useUpdatePartFinderBatchName(batch.id);
  const clearMutation = useClearPartFinderBatchName(batch.id);

  const isPending = updateMutation.isPending || clearMutation.isPending;
  const activeError = updateMutation.error ?? clearMutation.error;

  useEffect(() => {
    if (isEditing) return;
    setValue(savedName);
  }, [savedName, isEditing]);

  const title = getBatchLabel(batch);

  if (!canEdit) {
    return (
      <TitleRow>
        <Typography variant='h6'>{title}</Typography>
      </TitleRow>
    );
  }

  const trimmed = value.trim();
  const isDirty = trimmed !== savedName.trim();
  const apiError = activeError ? getApiError(activeError, 'Failed to rename batch') : undefined;

  const resetMutations = () => {
    updateMutation.reset();
    clearMutation.reset();
  };

  const handleSave = () => {
    if (!isDirty) return;
    resetMutations();
    const onSuccess = () => {
      setValue(trimmed);
      setIsEditing(false);
    };
    if (trimmed === '') {
      clearMutation.mutate(undefined, { onSuccess });
    } else {
      updateMutation.mutate({ name: trimmed }, { onSuccess });
    }
  };

  const handleCancelEdit = () => {
    resetMutations();
    setValue(savedName);
    setIsEditing(false);
  };

  const handleStartEdit = () => {
    resetMutations();
    setIsEditing(true);
  };

  if (isEditing) {
    return (
      <Box>
        <FieldRow>
          <TextField
            value={value}
            onChange={event => setValue(event.target.value)}
            onKeyDown={event => {
              if (event.key === 'Enter') handleSave();
              if (event.key === 'Escape') handleCancelEdit();
            }}
            placeholder={defaultTitle}
            fullWidth
            size='small'
            disabled={isPending}
            autoFocus
            inputProps={{ 'aria-label': 'Batch name', maxLength: MAX_NAME_LENGTH }}
            slotProps={{
              input: {
                endAdornment: (
                  <InlineActions position='end'>
                    <Tooltip title='Cancel'>
                      <span>
                        <IconButton
                          size='small'
                          onClick={handleCancelEdit}
                          disabled={isPending}
                          aria-label='Cancel rename'
                        >
                          <CloseOutlined fontSize='small' />
                        </IconButton>
                      </span>
                    </Tooltip>
                    {isDirty && (
                      <Tooltip title='Save name'>
                        <span>
                          <IconButton
                            size='small'
                            color='primary'
                            onClick={handleSave}
                            disabled={isPending}
                            aria-label='Save name'
                          >
                            {isPending ? <CircularProgress size={16} /> : <CheckOutlined fontSize='small' />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </InlineActions>
                ),
              },
            }}
          />
        </FieldRow>
        {apiError && (
          <Alert severity='error' sx={{ marginTop: '8px' }}>
            {apiError.message}
          </Alert>
        )}
      </Box>
    );
  }

  return (
    <HoverRow>
      <Typography variant='h6'>{title}</Typography>
      <Box className='edit-affordance'>
        <Tooltip title='Rename batch'>
          <IconButton size='small' onClick={handleStartEdit} aria-label='Rename batch'>
            <EditOutlined fontSize='small' />
          </IconButton>
        </Tooltip>
      </Box>
    </HoverRow>
  );
};
